import "ol/ol.css";
import Map from "ol/Map";
import { defaults } from "ol/interaction/defaults";
import View from "ol/View";
import OSM from "ol/source/OSM";
import TileLayer from "ol/layer/Tile";
import ImageLayer from "ol/layer/Image";
import Static from "ol/source/ImageStatic";
import Feature from "ol/Feature";
import Point from "ol/geom/Point";
import VectorSource from "ol/source/Vector";
import { Icon, Stroke, Style } from "ol/style";
import VectorLayer from "ol/layer/Vector";
import Overlay from "ol/Overlay.js";
import { Fill, Text } from "ol/style.js";
import LineString from "ol/geom/LineString.js";

const initMap = (targetId, viewOption, controls = []) => {
  const mapView = new View(viewOption);
  const mapServer = new Map({
    layers: [],
    target: targetId,
    view: mapView,
    controls: controls,
    interactions: defaults({
      pinchRotate: false,
    }),
  });
  return { mapView, mapServer };
};
const addOSMLayer = (map, options) => {
  let layer = new TileLayer({
    preload: Infinity,
    source: new OSM(),
    ...options,
  });
  map.addLayer(layer);
  return map;
};

const createStaticImage = (sourceUrl, sourceExtent, options) => {
  const extent = sourceExtent; //[minx, miny, maxx, maxy]
  const staticImage = new Static({
    url: sourceUrl,
    projection: "EPSG:4326",
    imageExtent: extent,
    imageLoadFunction: (image) => {
      image.getImage().src = sourceUrl;
    },
    ...options,
  });
  return staticImage;
};
const createImageLayer = (staticImage) => {
  const imageLayer = new ImageLayer({
    source: staticImage,
  });
  return imageLayer;
};
const addImageLayer = (map, imageLayer) => {
  let layer = imageLayer;
  map.addLayer(layer);
  return map;
};

const createVectorLayer = (features) => {
  const mapVectorSource = new VectorSource({
    features: features, //[iconFeature]
  });
  return mapVectorSource;
};
const addVectorLayer = (map, mapVectorSource) => {
  let layer = new VectorLayer({
    source: mapVectorSource,
    className: "opvl",
    renderBuffer: 1545,
  });
  map.addLayer(layer);
  return map;
};
const createLineStringFeature = (points) => {
  const lineFeature = new Feature({
    geometry: new LineString(points), //[[121.757118, 24.724914]]
  });
  const lineStyle = new Style({
    stroke: new Stroke({
      color: "#AAA",
      width: 1,
      lineDash: [0.5, 5],
    }),
  });
  lineFeature.setStyle(lineStyle);
  return lineFeature;
};

const createIconFeature = (geometry, parameter, iconUrl, iconAnchor) => {
  const iconFeature = new Feature({
    geometry: new Point(geometry), //[121.757118, 24.724914]
    recordPoint: geometry,
    ...parameter,
  });
  const iconStyle = new Style({
    image: new Icon({
      anchor: iconAnchor,
      anchorXUnits: "pixels",
      anchorYUnits: "pixels",
      src: iconUrl,
      size: [96, 96],
      scale: 0.9,
    }),
  });
  iconFeature.setStyle(iconStyle);
  return iconFeature;
};
const createIconStyle = (iconUrl, iconAnchor) => {
  const iconStyle = new Style({
    image: new Icon({
      anchor: iconAnchor,
      anchorXUnits: "pixels",
      anchorYUnits: "pixels",
      src: iconUrl,
      size: [96, 96],
      scale: 0.9,
    }),
  });
  return iconStyle;
};
const createTextFeature = (
  geometry,
  parameter,
  { text, textAlign, justify }
) => {
  const textFeature = new Feature({
    geometry: new Point(geometry), //[121.757118, 24.724914]
    recordPoint: geometry,
    ...parameter,
  });
  const textStyle = new Style({
    //font = weight + ' ' + size + ' ' + font
    text: new Text({
      font: "400 18px Noto Sans TC",
      textAlign,
      justify,
      text,
      fill: new Fill({
        color: [255, 255, 255, 1],
      }),
      padding: [2, 2, 2, 2],
    }),
  });
  textFeature.setStyle(textStyle);
  return textFeature;
};
const createTextStyle = ({ text, textAlign, justify }) => {
  const textStyle = new Style({
    text: new Text({
      font: "16px Noto Sans TC",
      textAlign,
      justify,
      text,
      fill: new Fill({
        color: [255, 255, 255, 1],
      }),
      padding: [2, 2, 2, 2],
    }),
  });
  return textStyle;
};

const addOverlayLayer = (map, overlay) => {
  map.addOverlay(overlay);
  return map;
};
const createPopupOverlay = (container, positioning = "bottom-center") => {
  const overlay = new Overlay({
    element: container,
    autoPan: true,
    positioning: positioning,
    stopEvent: false, //點其屏蔽?
    autoPanAnimation: {
      duration: 250,
    },
  });
  return overlay;
};

const flyTo = (mapView, location, zoomOp, done) => {
  const duration = 800;
  const zoom = zoomOp ? zoomOp : mapView.getZoom();
  let parts = 2;
  let called = false;
  function callback(complete) {
    --parts;
    if (called) {
      return;
    }
    if (parts === 0 || !complete) {
      called = true;
      done(complete);
    }
  }
  mapView.animate(
    {
      center: location,
      duration: duration,
    },
    callback
  );
  mapView.animate(
    {
      zoom: zoom,
      duration: duration / 2,
    },
    callback
  );
};

const resetGeometry = (feature, geometry) => {
  feature.setGeometry(new Point(geometry));
};

export default {
  initMap,
  addOSMLayer,
  createStaticImage,
  createImageLayer,
  addImageLayer,
  createVectorLayer,
  addVectorLayer,
  createLineStringFeature,
  createIconFeature,
  createIconStyle,
  createTextFeature,
  createTextStyle,
  addOverlayLayer,
  createPopupOverlay,
  flyTo,
  resetGeometry,
};
